var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TvSymbolConvertor } from './Convertors/TvSymbolConvertor';
import { TvAccountsManager } from './Managers/TvAccountsManager';
import { TvOrdersManager } from './Managers/TvOrdersManager';
import { TvBrokerConfigFlags } from './BrokerConfig/TvBrokerConfigFlags';
import { TvPipsValuesCache } from './Caches/TvPipsValuesCache';
import { TvPriceFormatter } from './Formatters/TvPriceFormatter';
import { TvSpreadFormatter } from './Formatters/TvSpreadFormatter';
import { TvQuantityFormatter } from './Formatters/TvQuantityFormatter';
import { TvPositionsManager } from './Managers/TvPositionsManager';
import { TvOrderColumns } from './Columns/TvOrderColumns';
import { TvOrderHistoryColumns } from './Columns/TvOrderHistoryColumns';
import { TvPositionColumns } from './Columns/TvPositionColumns';
import { TvExecutionsManager } from './Managers/TvExecutionsManager';
import { TvQuoteRealtimeManager } from './Managers/TvQuoteRealtimeManager';
import { TvOrderPreview } from './Preview/TvOrderPreview';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { EventEmitter } from 'events';
import { TvNotificationManager } from './Managers/TvNotificationManager';
import { TvOrdersHistoryManager } from './Managers/TvOrdersHistoryManager';
import { TvAccountHelper } from './Helpers/TvAccountHelper';
import { TvRuleValidator } from './BrokerConfig/TvRuleValidator';
import { ApplicationInfo } from '../ApplicationInfo';
export class TvBrokerTerminal {
    constructor(host, dataFeed) {
        this.subscirbeGUID = 'TvBrokerTerminal';
        this._eventEmitter = new EventEmitter();
        this.accountsManager = new TvAccountsManager(host);
        this.ordersManager = new TvOrdersManager(host);
        this.positionsManager = new TvPositionsManager(host);
        this.executionManager = new TvExecutionsManager(host);
        this.quoteRealtimeManager = new TvQuoteRealtimeManager(host);
        this.notificationManager = new TvNotificationManager(host);
        this.historyOrdersManager = new TvOrdersHistoryManager();
        this.ruleValidator = new TvRuleValidator(host);
        this.dataFeed = dataFeed;
        this.host = host;
        this.startInterval();
    }
    subscribeRealtime(symbol) {
        this.quoteRealtimeManager.subscribeRealtime(symbol);
    }
    unsubscribeRealtime(symbol) {
        this.quoteRealtimeManager.unsubscribeRealtime(symbol);
    }
    subscribeDOM(symbol) {
        // ?
        this.dataFeed.subscribeDepth(symbol, (data) => {
            this.host.domUpdate(symbol, data);
        });
    }
    unsubscribeDOM(symbol) {
        // ?
        this.dataFeed.unsubscribeDepth(symbol);
    }
    placeOrder(order, confirmId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            return yield this.ordersManager.placeOrder(order, confirmId);
        });
    }
    previewOrder(order) {
        return __awaiter(this, void 0, void 0, function* () {
            const previewResult = yield TvOrderPreview.previewOrder(order);
            return previewResult;
        });
    }
    modifyOrder(order, confirmId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            yield this.ordersManager.modifyOrder(order, confirmId);
        });
    }
    cancelOrder(orderId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            yield this.ordersManager.cancelOrder(orderId);
        });
    }
    cancelOrders(symbol, side, ordersIds) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            yield this.ordersManager.cancelOrders(symbol, side, ordersIds, PlacedFrom.TV_TRADING_PLATFORM_DOM);
        });
    }
    reversePosition(positionId) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    closePosition(positionId, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            yield this.positionsManager.closePosition(positionId, amount);
        });
    }
    closeTrade(tradeId, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    editPositionBrackets(positionId, brackets, customFields) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkExploreMode();
            yield this.ordersManager.editPositionBrackets(positionId, brackets, customFields);
        });
    }
    editTradeBrackets(tradeId, brackets) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    leverageInfo(leverageInfoParams) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    setLeverage(leverageSetParams) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    previewLeverage(leverageSetParams) {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            throw new Error('Method not implemented.');
        });
    }
    subscribeEquity() {
        this.accountsManager.subscribeEquity();
    }
    subscribeMarginAvailable(symbol) {
        this.accountsManager.subscribeMarginAvailable(symbol);
    }
    unsubscribeMarginAvailable(symbol) {
        this.accountsManager.unsubscribeMarginAvailable(symbol);
    }
    subscribePipValue(symbol) {
        TvPipsValuesCache.subscribePipValue(symbol);
    }
    unsubscribePipValue(symbol) {
        TvPipsValuesCache.unsubscribePipValue(symbol);
    }
    unsubscribeEquity() {
        this.accountsManager.unsubscribeEquity();
    }
    chartContextMenuActions(context, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.ruleValidator.isTradePosible() ? yield this.host.defaultContextMenuActions(context, options) : [];
        });
    }
    isTradable(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            const isTradable = true;
            return yield Promise.resolve(isTradable);
        });
    }
    connectionStatus() {
        return 1 /* ConnectionStatus.Connected */;
    }
    orders() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.ordersManager.getOrders();
        });
    }
    ordersHistory() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.historyOrdersManager.ordersHistory();
        });
    }
    positions() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.positionsManager.getPositions();
        });
    }
    individualPositions() {
        return __awaiter(this, void 0, void 0, function* () {
            // ?
            const trades = [];
            return yield Promise.resolve(trades);
        });
    }
    executions(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executionManager.executions(symbol);
        });
    }
    symbolInfo(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield TvSymbolConvertor.getInstrumentInfoAsync(symbol, TvAccountHelper.getCurrentAccount());
        });
    }
    accountManagerInfo() {
        const orderSortingParams = {
            property: 'date',
            asc: false
        };
        const accountInfo = {
            accountTitle: 'ACCOUNT_TITLE', // TODO: not displayed?
            summary: this.accountsManager.getSummary(),
            customFormatters: this.accountsManager.getCustomFormatters(),
            orderColumns: TvOrderColumns.getOrderColumns(),
            orderColumnsSorting: orderSortingParams,
            historyColumns: TvOrderHistoryColumns.getOrderHistoryColumns(),
            historyColumnsSorting: orderSortingParams,
            positionColumns: TvPositionColumns.getPositionColumns(),
            // tradeColumns?: AccountManagerColumn[];
            pages: this.accountsManager.getPages()
            // possibleOrderStatuses?: OrderStatus[];
            // marginUsed?: IWatchedValue<number>;
            // contextMenuActions?(contextMenuEvent: MouseEvent | TouchEvent, activePageActions: ActionMetaInfo[]): Promise<ActionMetaInfo[]>;
        };
        return accountInfo;
    }
    getSymbolSpecificTradingOptions(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield TvBrokerConfigFlags.getSymbolSpecificTradingOptions(symbol);
        });
    }
    formatter(symbol, alignToMinMove) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                const formatter = new TvPriceFormatter(symbol, alignToMinMove);
                resolve(formatter);
            });
        });
    }
    spreadFormatter(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                const formatter = new TvSpreadFormatter(symbol);
                resolve(formatter);
            });
        });
    }
    quantityFormatter(symbol) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                const formatter = new TvQuantityFormatter(symbol);
                resolve(formatter);
            });
        });
    }
    // async getOrderDialogOptions? (symbol: string): Promise<OrderDialogOptions> {
    //     throw new Error('Method not implemented.');
    // }
    // getPositionDialogOptions? (): PositionDialogOptions {
    //     throw new Error('Method not implemented.');
    // }
    // #region  Additional Accounts Methods
    accountsMetainfo() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.accountsManager.accountsMetainfo();
        });
    }
    currentAccount() {
        return TvAccountHelper.getCurrentAccountId();
    }
    setCurrentAccount(id) {
        const oldAccount = TvAccountHelper.getCurrentAccount();
        TvAccountHelper.setAccountById(id);
        const curAccount = TvAccountHelper.getCurrentAccount();
        this.quoteRealtimeManager.changeCurrentAccount();
        this.dataFeed.setCurrentAccount(curAccount);
        this.accountsManager.changeAccount();
        this.onUpdateAccount(curAccount, oldAccount);
    }
    updateHostCurrentAccount() {
        this.host.currentAccountUpdate();
    }
    // #region
    // #region Utils
    updateDataByTimer() {
        const account = TvAccountHelper.getCurrentAccount();
        if (isNullOrUndefined(account))
            return;
        this.accountsManager.updateAccountData();
        TvPipsValuesCache.updateSubscribedPipValues(this.host, account);
        this.ruleValidator.applyIfNeed();
        this.positionsManager.updatePositions();
        this.ordersManager.updateOrders();
    }
    startInterval() {
        this.intervalId = setInterval(() => {
            this.updateDataByTimer();
        }, 1000);
    }
    stopInterval() {
        if (isNullOrUndefined(this.intervalId))
            return;
        clearInterval(this.intervalId);
    }
    // #endregion Utils
    // #region IDestroyable
    destroy() {
        this.stopInterval();
        // this.accountsManager.destroy();
        this.ordersManager.destroy();
        this.positionsManager.destroy();
        this.executionManager.destroy();
        this.notificationManager.destroy();
    }
    // #endregion IDestroyable
    // #region Events
    subscribeOnUpdateAccount(callback) {
        this._eventEmitter.on('onUpdateAccount', callback);
    }
    unsubscribeOnUpdateAccount(callback) {
        this._eventEmitter.off('onUpdateAccount', callback);
    }
    onUpdateAccount(newValue, oldValue) {
        this._eventEmitter.emit('onUpdateAccount', newValue, oldValue);
    }
    // #endregion Events
    checkExploreMode() {
        return __awaiter(this, void 0, void 0, function* () {
            if (ApplicationInfo.isExploreMode) {
                yield Promise.reject(new Error('Please log in to start trading'));
            }
        });
    }
}
